import { useCallback, useMemo } from 'react';

import { useShowStoreFeeItem } from '@givebacks/api/store';
import {
  ConsumerStudentFee,
  useListConsumerStudentPayments,
  useShowConsumerFee,
} from '@givebacks/api/student';
import { useCreateCartAndCheckout, useShopRoutes } from '@givebacks/shop';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Affix,
  Alert,
  Box,
  Button,
  ButtonMantine,
  Container,
  Group,
  LoadingOverlay,
  Stack,
  Text,
  centsToDollar,
} from 'ui';
import { useDisclosure } from 'ui/Hooks';
import { IconCircleCheck, IconShoppingCart } from 'ui/Icons';

import { useIsFeeInCart } from '../../../../hooks/useIsFeeInCart';
import { AddPartialPaymentModal } from '../../../modals/AddPartialPaymentModal/AddPartialPaymentModal';
import { FeeImage } from '../../../molecules/cards/feeDisplay/FeeImage';
import { AddFeeToCartButton } from '../../buttons/AddFeeToCartButton';
import { FeeDetail } from './FeeDetail';
import classes from './FeeDetailLayout.module.css';
import { PaymentHistory } from './PaymentHistory';

type FeeDetailLayoutProps = {
  feeId: string;
};

type FeeHeaderProps = {
  fee: ConsumerStudentFee;
};

type FeeActionsProps = {
  studentFee: ConsumerStudentFee;
  feeTotal?: number;
};
interface FeeActionsPropsWithStoreItem extends FeeActionsProps {
  storeItemId: string;
  cartId?: string;
  cartItemId?: string;
}

interface AddFeeToCartActionsProps extends FeeActionsProps {
  storeItemId: string;
}

const FeeHeader = ({ fee }: FeeHeaderProps) => {
  const isFullyPaid = useMemo(() => {
    return fee.amount_remaining === 0;
  }, [fee.amount_remaining]);

  return (
    <Group>
      <FeeImage icon={fee.fee.icon} c={isFullyPaid ? 'mint.7' : 'gray.4'}>
        {isFullyPaid ? <IconCircleCheck size={32} /> : null}
      </FeeImage>
      <Stack gap={0}>
        <Text size="xl" style={{ wordBreak: 'break-word' }}>
          {fee.fee.name}
        </Text>
        <Text size="lg" c="gray.6">
          {isFullyPaid ? 'Paid' : centsToDollar(fee.amount_remaining)}
        </Text>
      </Stack>
    </Group>
  );
};

const FeePaymentHistory = ({ fee }: { fee: ConsumerStudentFee }) => {
  const { data } = useListConsumerStudentPayments({
    studentId: fee.student.uuid,
    queryOptions: {
      searches: {
        student_fee_id: {
          value: fee.uuid,
        },
      },
    },
  });
  if (!data?.payments || data.payments.length === 0) return null;
  return (
    <Stack gap={12}>
      <Text className={classes.SectionTitle}>Payment History</Text>
      {data?.payments ? (
        <PaymentHistory
          amountPaid={fee.amount_paid}
          payments={data?.payments}
          feeTotal={fee.amount}
          remainingBalance={fee.amount_remaining}
        />
      ) : null}
    </Stack>
  );
};

const FeeIsInCartActions = ({
  studentFee,
  feeTotal,
  storeItemId,
  cartId,
  cartItemId,
}: FeeActionsPropsWithStoreItem) => {
  const [value, handlers] = useDisclosure(false);
  const navigate = useNavigate();
  const allowsPartialPayment = useMemo(() => {
    return studentFee.fee.allow_partial_payment;
  }, [studentFee.fee.allow_partial_payment]);

  return (
    <Stack>
      <Alert.Collapse
        type="info-blue"
        title="Partial Payment has been added to Cart"
        icon={<IconShoppingCart size={16} />}
        in={allowsPartialPayment}
      >
        {centsToDollar(feeTotal)} out of the {centsToDollar(studentFee.amount)} has been added to
        your cart.
      </Alert.Collapse>

      <Group wrap="wrap" grow>
        {allowsPartialPayment && (
          <ButtonMantine variant="outline" miw="251px" onClick={handlers.open}>
            Update Payment Amount
          </ButtonMantine>
        )}
        <ButtonMantine onClick={() => navigate('/shop/carts')}>View Cart</ButtonMantine>
      </Group>
      <AddPartialPaymentModal
        studentFee={studentFee}
        opened={value}
        onClose={handlers.close}
        storeItemId={storeItemId}
        initialAmount={feeTotal}
        existingCartInfo={{
          cartId: cartId,
          cartItemId: cartItemId,
        }}
      />
    </Stack>
  );
};

/**
 * Renders a Pay Now button
 * The pay now button that will add the remaining amount to the cart and navigate to the checkout page
 */
const PayNowButton = ({
  studentFee,
  storeItemId,
}: {
  studentFee: ConsumerStudentFee;
  storeItemId: string;
}) => {
  const [value, handlers] = useDisclosure(false);
  const { navigateToCheckout } = useShopRoutes();

  const { createCartAndCheckout, isLoading: isCartCreateLoading } = useCreateCartAndCheckout({
    onCheckoutCreated: navigateToCheckout,
  });

  const cart = useMemo(() => {
    return {
      store_item_id: storeItemId,
      quantity: 1,
      price: studentFee.amount_remaining,
      student_fee_id: studentFee.uuid,
    };
  }, [storeItemId, studentFee.amount_remaining, studentFee.uuid]);

  const handlePayNowForNoPartialPayment = useCallback(() => {
    createCartAndCheckout({ storeId: studentFee.cause_id, cartItems: [cart] });
  }, [createCartAndCheckout, studentFee.cause_id, cart]);

  const onPartialPayment = useCallback(() => {
    handlers.open();
  }, [handlers]);

  const handlePayNow = useCallback(() => {
    if (studentFee.fee.allow_partial_payment) {
      onPartialPayment();
    } else {
      handlePayNowForNoPartialPayment();
    }
  }, [handlePayNowForNoPartialPayment, onPartialPayment, studentFee.fee.allow_partial_payment]);

  return (
    <>
      <Button onClick={handlePayNow} loading={isCartCreateLoading}>
        Pay Now
      </Button>
      <AddPartialPaymentModal
        studentFee={studentFee}
        opened={value}
        onClose={handlers.close}
        storeItemId={storeItemId}
        isPayNow={true}
      />
    </>
  );
};

const AddFeeToCartActions = ({ studentFee, storeItemId }: AddFeeToCartActionsProps) => {
  const [value, handlers] = useDisclosure(false);

  return (
    <>
      <Group wrap="wrap" grow>
        <AddFeeToCartButton
          studentFee={studentFee}
          buttonProps={{ size: 'lg', variant: 'outline' }}
        />
        <PayNowButton studentFee={studentFee} storeItemId={storeItemId} />
      </Group>
      <AddPartialPaymentModal
        studentFee={studentFee}
        opened={value}
        onClose={handlers.close}
        storeItemId={storeItemId}
        isPayNow={true}
      />
    </>
  );
};

const FeeActions = ({ studentFee }: FeeActionsProps) => {
  const { data: storeItem } = useShowStoreFeeItem({
    feeId: studentFee.fee.uuid,
    causeId: studentFee.cause.uuid,
  });

  const { isStudentFeeInCart, feeTotal, cartId, cartItemId } = useIsFeeInCart({
    studentFeeId: studentFee.uuid,
    causeId: studentFee.cause_id,
  });

  if (!storeItem) {
    return null;
  }

  if (isStudentFeeInCart) {
    return (
      <FeeIsInCartActions
        studentFee={studentFee}
        feeTotal={feeTotal}
        storeItemId={storeItem.uuid}
        cartId={cartId}
        cartItemId={cartItemId}
      />
    );
  }
  return <AddFeeToCartActions studentFee={studentFee} storeItemId={storeItem.uuid} />;
};

const FeeActionsGuard = ({ studentFee }: FeeActionsProps) => {
  const isFullyPaid = useMemo(() => {
    return studentFee.amount_remaining === 0;
  }, [studentFee.amount_remaining]);

  if (isFullyPaid) {
    return null;
  }

  return <FeeActions studentFee={studentFee} />;
};

const FeeDetails = ({ fee }: { fee: ConsumerStudentFee }) => {
  const isVariableFee = useMemo(() => {
    return fee.fee.type.toLowerCase().includes('variable');
  }, [fee.fee.type]);

  return (
    <Stack gap={12}>
      <Text className={classes.SectionTitle}>Fee Details</Text>
      <FeeDetail fee={fee} />
      <Text fz="sm">{fee.fee.description}</Text>
      {isVariableFee ? <Text fz="sm">{fee.description}</Text> : null}
    </Stack>
  );
};

const FeeDetailLayout = ({ feeId }: FeeDetailLayoutProps) => {
  const { data, isLoading, error } = useShowConsumerFee({ feeId });

  if (error) {
    return <Navigate to="/student-fees" />;
  }

  if (isLoading || !data) {
    return <LoadingOverlay visible />;
  }
  return (
    <Container size="sm">
      {/* Add base padding to the stack so that the affix is not cut off */}
      <Stack pb={{ base: 160, md: 0 }}>
        <FeeHeader fee={data.student_fee} />
        <Box visibleFrom="sm">
          <FeeActionsGuard studentFee={data.student_fee} />
        </Box>
        <FeeDetails fee={data.student_fee} />
        <FeePaymentHistory fee={data.student_fee} />
        <Affix hiddenFrom="sm" position={{ bottom: 0 }} w="100%" p={'md'} bg="white">
          <FeeActionsGuard studentFee={data.student_fee} />
        </Affix>
      </Stack>
    </Container>
  );
};

export { FeeDetailLayout };
